import { useState } from 'react';
import MinusSquare from 'icons/MinusSquare';
import PlusSquare from 'icons/PlusSquare';
import Button from 'components/Globals/Base/Button';
import Dropdown from 'components/Globals/Base/Dropdown';
import Checkbox from 'components/Globals/Base/Checkbox';
import type { SearchFilter } from 'interfaces/components/SearchFilter';
import SearchWButton from 'components/Globals/SearchWButton';
import YearFilter from 'components/Globals/SlideFilter';
import Heading from 'styles/typography/Heading';
import { gridResponsiveSettings } from 'components/Globals/SlideFilter/responsiveSettings';
import toKebabCase from 'lodash/kebabCase';
import { RANKING_V2_PAGE } from 'constants/testsIds/rankingV2Page';
import Placeholder from 'components/Placeholder';
import { SearchWrapper, FiltersWrapper, FiltersHeader, FiltersContainer } from './FiltersStyles';

export interface FiltersProps {
  filters: SearchFilter[];
  years: string[];
  activeFilters: { [key: string]: string | boolean };
  activeYear: string;
  search: string;
  updateFilters: (type: 'ADD' | 'DELETE', fieldName: string, value: string | boolean) => void;
  resetFilters: () => void;
  updateSearch: (value: string) => void;
  updateYear: (value: string) => Promise<boolean>;
  showSkeleton: boolean;
  isMobile?: boolean;
}

const Filters = ({
  filters,
  years,
  activeFilters,
  activeYear,
  search,
  updateFilters,
  resetFilters,
  updateSearch,
  updateYear,
  showSkeleton = false,
  isMobile,
}: FiltersProps) => {
  const [display, setDisplay] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const checkboxFields = filters?.filter(
    (item) =>
      item.type === 'checkbox' ||
      (item.options && item.options.length <= 2 && (item.options.includes('yes') || item.options.includes('no'))),
  );

  const selectMenuFields = filters?.filter(
    (item) => !checkboxFields.includes(item) && !(item.title === 'Name' || item.title === 'Company'),
  );

  const changeFilterDisplay = () => {
    setDisplay((prevValue) => !prevValue);
  };

  const handleClick = () => {
    setIsExpanded(false);
  };

  let filtersHeight = 0;
  if (filters) {
    filtersHeight = isMobile
      ? 169 + (filters.length - checkboxFields.length) * 80 + checkboxFields.length * 32
      : 157 + Math.ceil((filters.length - checkboxFields.length) / 4) * 92 + Math.ceil(checkboxFields.length / 4) * 40;
  }

  return (
    <>
      <SearchWrapper>
        <SearchWButton
          initialValue={search}
          dataCy={RANKING_V2_PAGE.SEARCH}
          onChange={(value) => updateSearch(value)}
          $variant='underlined'
        />
        <YearFilter
          className='year-filter'
          valueList={years}
          dataCy={RANKING_V2_PAGE.YEAR_FILTER}
          selectedValue={activeYear}
          responsiveSettings={gridResponsiveSettings}
          changeSelectedValue={(newYear) => updateYear(newYear)}
        />
      </SearchWrapper>
      <FiltersWrapper>
        {filters &&
          !!filters.length &&
          (showSkeleton ? (
            <Placeholder size={{ height: filtersHeight, width: '100%' }} />
          ) : (
            <>
              <FiltersHeader $display={display}>
                <Heading
                  className='title'
                  as='h2'
                  $fontFamily='graphikCond'
                  $size={{ default: 'level4' }}
                  data-cy={RANKING_V2_PAGE.FILTER_TITLE}
                >
                  <button
                    type='button'
                    aria-label='Open and close filters'
                    data-cy={RANKING_V2_PAGE.OPEN_CLOSE_FILTER}
                    onClick={changeFilterDisplay}
                    className='focus:outline-none'
                  >
                    {display ? <MinusSquare /> : <PlusSquare />}
                  </button>{' '}
                  Filters
                </Heading>
                <Button
                  className={`${!Object.keys(activeFilters).length && !search ? 'hidden' : ''}`}
                  variant='secondary'
                  onClick={resetFilters}
                >
                  RESET FILTERS
                </Button>
              </FiltersHeader>
              {display && (
                <>
                  <FiltersContainer data-cy={RANKING_V2_PAGE.FILTER_FORM_DROPDOWN}>
                    {selectMenuFields.map((field) => {
                      const { fieldName, title, options } = field;
                      const optionsList = ['All', ...(options || [])];
                      return (
                        <Dropdown
                          key={title}
                          dataCy={`${toKebabCase(title)}-ddl`}
                          isExpanded={isExpanded}
                          onBtnClick={handleClick}
                          onOptionClick={(value) => updateFilters(value !== 'All' ? 'ADD' : 'DELETE', fieldName, value)}
                          list={optionsList}
                          selected={activeFilters[fieldName]?.toString() || ''}
                          size='small'
                          className='dropdown'
                          label={title}
                          placeholder={`Choose a ${title}`}
                        />
                      );
                    })}
                  </FiltersContainer>
                  <FiltersContainer data-cy={RANKING_V2_PAGE.FILTER_FORM_CHECKOUT}>
                    {checkboxFields.map((field) => {
                      const { fieldName, title } = field;
                      return (
                        <Checkbox
                          className='checkbox'
                          key={`${title}`}
                          dataCy={`${toKebabCase(title)}-cb`}
                          name={title}
                          // @ts-ignore
                          value={activeFilters[fieldName] || false}
                          onChange={(value) => updateFilters(value ? 'ADD' : 'DELETE', fieldName, value)}
                        >
                          {title}
                        </Checkbox>
                      );
                    })}
                  </FiltersContainer>
                </>
              )}
            </>
          ))}
      </FiltersWrapper>
    </>
  );
};
export default Filters;
